/// <reference types="google_interactive_media_ads_types" />

/**
 * Copyright (C) Firstlight Media.
 * All rights reserved.
 */

import { AD_REQUEST_CONTEXT } from '../../../models/csai/AdRequestContext';
import { QPPrefetchConfig } from '../../../models/csai/QPPrefetchConfig';
import { AdsRenderingSettings } from '../../../models/AdsRenderingSettings';
import { IMAAdRequest } from '../../../models/csai/IMAAdRequest';
import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { ImaSdkSettings } from '../../../models/ImaSdkSettings';
import { setImaSdkSettings } from '../../util/AdUtil';
import { ContentAdsManagerResolver } from './ContentAdsManagerResolver';
import { AdsManagerRole, QPAdsManager, QPAdsManagerOptions } from './QPAdsManager';
import { QPPrefetchManager } from './QPPrefetchManager';
import { AFCPlaybackSession } from '@quickplay/player';

export interface QPAdsManagerLoaderConfig {
  containerElement: Element;
  videoElement: HTMLVideoElement;
  imaSDK: typeof google.ima;
  request: IMAAdRequest;
  renderingSettings?: AdsRenderingSettings;
  configuredLogger: Logger;
  onAdError?: (error: unknown) => void;
  isPrefetchEnabled?: boolean;
  imaSdkSettings?: ImaSdkSettings;
  adFrequencySession?: AFCPlaybackSession;
  onAdsManagerActivated?: (role: AdsManagerRole) => void;
}

/**
 * QPAdsLoader
 *
 * Instance service responsible for managing the application-session
 * lifecycle of the Google IMA AdsLoader and AdDisplayContainer.
 *
 * AdsLoader and AdDisplayContainer are created once and reused
 * across multiple playback sessions.
 *
 * Supports:
 * - Pre-roll prefetch
 * - Content ad request containing pre-roll, mid-roll and post-roll
 * - Parallel AdsManager loading
 */
class QPAdsLoaderService {
  private adsLoader: google.ima.AdsLoader | null = null;
  private adDisplayContainer: google.ima.AdDisplayContainer | null = null;
  private imaSDK: typeof google.ima | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private initialized = false;
  private prefetchManagerInstance: QPPrefetchManager | null = null;
  private logger: Logger = createLogger(LoggerLevel.OFF);

  /* -------------------------------------------------------------------------- */
  /* Initialization                                                             */
  /* -------------------------------------------------------------------------- */

  /**
   * Initializes the shared IMA AdsLoader and AdDisplayContainer.
   */
  private initialize(
    imaSDK: typeof google.ima,
    containerElement: Element,
    videoElement: HTMLVideoElement,
    imaSettingsConfiguration?: ImaSdkSettings,
  ): void {
    if (this.initialized) {
      return;
    }

    if (!imaSDK) {
      throw new Error('[QPAdsLoader] IMA SDK is required');
    }

    if (!containerElement) {
      throw new Error('[QPAdsLoader] containerElement is required');
    }

    if (!videoElement) {
      throw new Error('[QPAdsLoader] videoElement is required');
    }

    const imaSdkSettings = imaSDK.settings;

    if (imaSettingsConfiguration && imaSdkSettings) {
      setImaSdkSettings(imaSdkSettings, imaSettingsConfiguration);
    }

    this.adDisplayContainer = new imaSDK.AdDisplayContainer(containerElement, videoElement);

    this.adsLoader = new imaSDK.AdsLoader(this.adDisplayContainer);
    this.logger?.log('[QPAdsLoader] AdsLoader initialized');
    this.imaSDK = imaSDK;
    this.videoElement = videoElement;

    this.initialized = true;
  }

  /* -------------------------------------------------------------------------- */
  /* Prefetch                                                                    */
  /* -------------------------------------------------------------------------- */

  /**
   * Prefetches the pre-roll AdsManager.
   *
   * The AdsManager is loaded early and kept ready until
   * it is consumed by requestAdsForPlayer().
   */
  public initAdsLoader(config: QPPrefetchConfig): void {
    this.logger = config.logger ?? createLogger(LoggerLevel.OFF);
    if (!this.initialized) {
      this.initialize(config.imaSDK, config.containerElement, config.videoElement, config.imaSdkSettings);
    }

    const { adsLoader, imaSDK } = this.getInitializedImaSession();

    this.prefetchManagerInstance = new QPPrefetchManager({
      adsLoader,
      adRequest: config.adRequest,
      adsRenderingSettings: config.adsRenderingSettings,
      imaSDK,
      videoElement: this.videoElement as HTMLVideoElement,
      imaSdkSettings: config.imaSdkSettings,
      logger: this.logger,
    });
    this.logger.log('[QPAdsLoader] Prefetch manager initialized');
  }

  /**
   * Returns the shared IMA AdsLoader used by the application session.
   */
  public getSharedAdsLoader(): any {
    return this.adsLoader;
  }

  /* -------------------------------------------------------------------------- */
  /* Public API                                                                  */
  /* -------------------------------------------------------------------------- */

  /**
   * Requests ads for the player.
   *
   * If a pre-roll AdsManager was prefetched:
   *
   * 1. Consume prefetched pre-roll.
   * 2. Start prefetched pre-roll.
   * 3. Request content ads in parallel.
   * 4. Upload the content AdsManager as the next manager.
   *
   * The content request can contain:
   * - pre-roll
   * - mid-roll
   * - post-roll
   *
   * If no prefetch exists, the content AdsManager is requested directly.
   */
  public createComposedQPAdsManager(config: QPAdsManagerLoaderConfig): Promise<QPAdsManager> {
    const effectiveImaSettings = {
      ...config.imaSdkSettings,
      ...(config.adFrequencySession ? { autoPlayAdBreaks: false } : {}),
    };

    if (!this.initialized) {
      this.initialize(config.imaSDK, config.containerElement, config.videoElement, effectiveImaSettings);
    } else if (this.imaSDK?.settings && effectiveImaSettings) {
      setImaSdkSettings(this.imaSDK.settings, effectiveImaSettings);
    }

    const { adsLoader, imaSDK } = this.getInitializedImaSession();

    this.adDisplayContainer?.initialize();

    const fullManagerOptions: QPAdsManagerOptions = {
      request: config.request,
      imaSDK,
      logger: config.configuredLogger,
      onAdError: config.onAdError,
      autoPlayAdBreaks: !config.adFrequencySession,
      onAdsManagerActivated: config.onAdsManagerActivated,
    };

    const contentAdsManagerResolver = new ContentAdsManagerResolver({
      adsLoader,
      adRequest: config.request,
      adsRenderingSettings: config.renderingSettings,
      imaSDK,
      videoElement: this.videoElement as HTMLVideoElement,
      requestContext: AD_REQUEST_CONTEXT.CONTENT,
      logger: config.configuredLogger,
      imaSdkSettings: config.imaSdkSettings,
    });

    if (config.isPrefetchEnabled && this.prefetchManagerInstance) {
      return Promise.resolve(
        new QPAdsManager(this.prefetchManagerInstance, contentAdsManagerResolver, fullManagerOptions),
      );
    }

    return Promise.resolve(new QPAdsManager(null, contentAdsManagerResolver, fullManagerOptions));
  }

  /* -------------------------------------------------------------------------- */
  /* Reset                                                                       */
  /* -------------------------------------------------------------------------- */

  /**
   * Resets QPAdsLoader state.
   *
   * Intended for testing or complete application-session teardown.
   */
  public deinitAdsLoader(): void {
    this.logger.log('[QPAdsLoader] De-initializing AdsLoader');
    this.prefetchManagerInstance = null;

    try {
      this.adsLoader?.destroy();
      this.adDisplayContainer?.destroy();
    } catch {
      // Ignore destroy errors.
    }

    this.adsLoader = null;
    this.adDisplayContainer = null;
    this.imaSDK = null;
    this.videoElement = null;
    this.initialized = false;
  }

  private getInitializedImaSession(): { adsLoader: google.ima.AdsLoader; imaSDK: typeof google.ima } {
    if (!this.adsLoader || !this.imaSDK) {
      throw new Error('[QPAdsLoader] IMA AdsLoader is not initialized');
    }

    return {
      adsLoader: this.adsLoader,
      imaSDK: this.imaSDK,
    };
  }
}

export const QPAdsLoader = new QPAdsLoaderService();
